import React from "react";
import { Box, Typography } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import { NumericFormat } from "react-number-format";
import StyleVariables from "../../assets/styles/Variable.module.scss";
import type { BillSouvenir as BillSouvenirModel } from "../../models/Souvenir";

type Props = {
  souvenirs?: BillSouvenirModel[] | null;
  maxNameLength?: number;
  layout?: "rows" | "inline";
  showBorderTop?: boolean;
  showBorderBottom?: boolean;
  variant?: TypographyProps["variant"];
  fontSize?: number;
  lineHeight?: string;
  rowGap?: number;
  color?: string;
};

const Souvenirs = ({
  souvenirs = [],
  maxNameLength = 15,
  layout = "rows",
  showBorderTop = false,
  showBorderBottom = true,
  variant = "subtitle2",
  fontSize,
  lineHeight,
  rowGap = 1,
  color = StyleVariables["color-text"],
}: Props) => {
  const items = Array.isArray(souvenirs)
    ? souvenirs.filter((item) => item.quantity > 0)
    : [];

  if (!items.length) return null;

  const formatSouvenirName = (name: string) => {
    if (maxNameLength === undefined) return name;

    let result = "";
    let count = 0;

    for (const character of Array.from(name)) {
      const isThaiMark =
        /[\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]/.test(character);

      if (!isThaiMark) {
        if (count >= maxNameLength) {
          return `${result}...`;
        }

        count += 1;
      }

      result += character;
    }

    return result;
  };

  return (
    <Box
      marginTop={layout === "inline" ? 0 : 2}
      marginBottom={2}
      paddingTop={layout === "inline" ? 0 : showBorderTop ? 2 : 0}
      paddingBottom={showBorderBottom ? 2 : 0}
      color={color}
      sx={{ minWidth: 0, maxWidth: "100%" }}
      borderTop={
        showBorderTop
          ? `1px dashed ${StyleVariables["color-border"]}`
          : undefined
      }
      borderBottom={
        showBorderBottom
          ? `1px dashed ${StyleVariables["color-border"]}`
          : undefined
      }
    >
      {layout === "inline" ? (
        <Typography
          variant={variant}
          fontWeight={"regular"}
          fontSize={fontSize}
          lineHeight={lineHeight}
          sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
        >
          แถม{" "}
          {items.map((item, index) => (
            <React.Fragment key={item.souvenirCode}>
              {index > 0 && ", "}
              {formatSouvenirName(item.souvenirName || item.souvenirCode)}{" "}
              <NumericFormat
                value={item.quantity}
                displayType="text"
                thousandSeparator
              />
              {item.unit ? ` ${item.unit}` : ""}
            </React.Fragment>
          ))}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) max-content fit-content(25%)",
            columnGap: 1,
            rowGap,
            alignItems: "start",
          }}
        >
          {items.map((item) => (
            <React.Fragment key={item.souvenirCode}>
              <Typography
                variant={variant}
                fontWeight={"regular"}
                fontSize={fontSize}
                lineHeight={lineHeight}
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                }}
              >
                แถม {formatSouvenirName(item.souvenirName || item.souvenirCode)}
              </Typography>

              <Typography
                variant={variant}
                fontWeight={"regular"}
                fontSize={fontSize}
                lineHeight={lineHeight}
                textAlign={"right"}
                sx={{
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <NumericFormat
                  value={item.quantity}
                  displayType="text"
                  thousandSeparator
                />
              </Typography>

              <Typography
                variant={variant}
                fontWeight={"regular"}
                fontSize={fontSize}
                lineHeight={lineHeight}
                textAlign={"right"}
                sx={{ minWidth: 0, overflowWrap: "anywhere" }}
              >
                {item.unit || ""}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Souvenirs;